import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  DollarSign,
  TrendingUp,
  Plus,
  RefreshCw
} from 'lucide-react';
import apiService from './services/api';
import { Panel, Button, LoadingSpinner, CreateAppointmentModal } from './components';

// Date utility functions
const formatDateLocal = (date) => {
  // Format date as YYYY-MM-DD in local timezone (not UTC)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCurrentDate = () => {
  return formatDateLocal(new Date());
};

const getCurrentWeek = () => {
  // ISO week number calculation (weeks start on Monday, week 1 contains first Thursday)
  const today = new Date();
  // Create a copy and set to nearest Thursday (current date + 4 - current day number)
  // Make Sunday's day number 7 instead of 0
  const target = new Date(today.valueOf());
  const dayNum = (today.getDay() + 6) % 7; // Monday=0, Sunday=6
  target.setDate(target.getDate() - dayNum + 3); // Set to Thursday of current week
  const firstThursday = new Date(target.getFullYear(), 0, 4); // Jan 4 is always in week 1
  const weekNum = 1 + Math.round(((target - firstThursday) / 86400000) / 7);
  return weekNum;
};

const getCurrentMonth = () => {
  return new Date().getMonth() + 1; // getMonth() returns 0-11
};

const getCurrentYear = () => {
  return new Date().getFullYear();
};

function AppointmentsPage() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    apiService.get('/auth/profile/').then(u => setUserRole(u.role)).catch(() => {});
  }, []);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [pageSize] = useState(50);

  // Filter states
  const [dateFilterType, setDateFilterType] = useState('date'); // 'date', 'week', 'month', 'year'
  const [selectedDate, setSelectedDate] = useState(getCurrentDate());
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState('');
  const [selectedBookedBy, setSelectedBookedBy] = useState('');

  // Quick filters
  const [showOnlyUnpaid, setShowOnlyUnpaid] = useState(false);
  const [showOnlyUnmatched, setShowOnlyUnmatched] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'standard', 'alf', 'event'
  const [showCancelled, setShowCancelled] = useState(false);
  const [futureFilter, setFutureFilter] = useState('all'); // 'all', 'rebooked', 'not_rebooked'

  // Dropdown data
  const [locations, setLocations] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    fetchLocations();
    fetchTechnicians();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filters change
    fetchAppointments(1);
  }, [dateFilterType, selectedDate, selectedWeek, selectedMonth, selectedYear, selectedLocation, selectedTechnician, selectedBookedBy, showOnlyUnpaid, showOnlyUnmatched, typeFilter, showCancelled, futureFilter]);

  useEffect(() => {
    if (currentPage > 1) {
      fetchAppointments(currentPage);
    }
  }, [currentPage]);

  const fetchLocations = async () => {
    try {
      const data = await apiService.getLocations();
      setLocations(data.results || data || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const data = await apiService.getStaff({ is_technician: true });
      const allTechs = data.results || data || [];
      setTechnicians(allTechs.filter(t => !t.internal_status || t.internal_status === 'active'));
    } catch (err) {
      console.error('Error fetching technicians:', err);
    }
  };

  const fetchAppointments = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      // Build filter params
      const params = {};

      // Date filtering
      if (dateFilterType === 'date' && selectedDate) {
        params.date_time__date = selectedDate;
        params.page = page; // Use pagination for single date
      } else if (dateFilterType === 'week' && selectedWeek) {
        params.week = selectedWeek;
        params.year = selectedYear;
      } else if (dateFilterType === 'month' && selectedMonth) {
        params.month = selectedMonth;
        params.year = selectedYear;
      } else if (dateFilterType === 'year' && selectedYear) {
        params.year = selectedYear;
      }

      // Location and technician filtering
      if (selectedLocation) {
        params.technician__location = selectedLocation;
      }
      if (selectedTechnician) {
        params.technician = selectedTechnician;
      }
      // booked_by filter applied client-side after fetch

      // For week/month/year filters, fetch all pages
      let allAppointments = [];
      if (dateFilterType !== 'date') {
        let currentPage = 1;
        let hasMore = true;
        while (hasMore) {
          const data = await apiService.getAppointments({ ...params, page: currentPage });
          const pageResults = data.results || data || [];
          allAppointments = [...allAppointments, ...pageResults];
          hasMore = !!data.next;
          currentPage++;
          // Safety limit to prevent infinite loops
          if (currentPage > 20) break;
        }
      } else {
        const data = await apiService.getAppointments(params);
        allAppointments = data.results || data || [];
        setHasNext(!!data.next);
        setHasPrevious(!!data.previous);
      }

      // Compute amounts_match for each appointment
      allAppointments = allAppointments.map(appt => ({
        ...appt,
        amounts_match: appt.total_payment && appt.expected_revenue
          ? Math.abs(parseFloat(appt.total_payment) - parseFloat(appt.expected_revenue) - parseFloat(appt.technician_tip || 0)) < 0.01
            || Math.abs(parseFloat(appt.total_payment) - parseFloat(appt.expected_revenue)) < 0.01
          : null
      }));

      // Filter cancelled unless toggled on
      let filteredAppointments = showCancelled
        ? allAppointments
        : allAppointments.filter(appt => !appt.status?.toUpperCase().includes('CANCEL'));

      // Booked by filter
      if (selectedBookedBy) {
        filteredAppointments = filteredAppointments.filter(appt => appt.booked_by_name === selectedBookedBy);
      }

      // Apply quick filters
      if (showOnlyUnpaid) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Start of today
        filteredAppointments = filteredAppointments.filter(appt => {
          if (appt.is_paid) return false; // Exclude paid
          // Exclude today and future appointments
          const apptDate = new Date(appt.date_time);
          apptDate.setHours(0, 0, 0, 0);
          return apptDate < today;
        });
      }
      if (showOnlyUnmatched) {
        filteredAppointments = filteredAppointments.filter(appt => appt.amounts_match === false);
      }

      // Type filter - use appointment_type from API (computed from services)
      if (typeFilter !== 'all') {
        filteredAppointments = filteredAppointments.filter(appt => {
          const type = appt.appointment_type || (appt.is_alf ? 'ALF' : appt.is_event ? 'Event' : 'Standard');
          if (typeFilter === 'alf') return type === 'ALF';
          if (typeFilter === 'event') return type === 'Event';
          if (typeFilter === 'standard') return type === 'Standard';
          return true;
        });
      }

      // Future appointment filter
      if (futureFilter === 'rebooked') {
        filteredAppointments = filteredAppointments.filter(appt => appt.has_future_appointment);
      } else if (futureFilter === 'not_rebooked') {
        filteredAppointments = filteredAppointments.filter(appt => !appt.has_future_appointment);
      }

      // Sort by date ASC, then time ASC for same date
      filteredAppointments.sort((a, b) => {
        const dateA = new Date(a.date_time);
        const dateB = new Date(b.date_time);
        return dateA - dateB;
      });

      // Handle response
      setAppointments(filteredAppointments);
      setTotalCount(filteredAppointments.length);

      // No pagination controls needed for week/month/year (we fetched all)
      if (dateFilterType !== 'date') {
        setHasNext(false);
        setHasPrevious(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch appointments');
      console.error('Error fetching appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (hasNext) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (hasPrevious) {
      setCurrentPage(prev => prev - 1);
    }
  };

  // Date navigation helpers
  const goToPreviousDay = () => {
    const currentDate = new Date(selectedDate + 'T12:00:00'); // Noon to avoid DST issues
    currentDate.setDate(currentDate.getDate() - 1);
    setSelectedDate(formatDateLocal(currentDate));
  };

  const goToNextDay = () => {
    const currentDate = new Date(selectedDate + 'T12:00:00'); // Noon to avoid DST issues
    currentDate.setDate(currentDate.getDate() + 1);
    setSelectedDate(formatDateLocal(currentDate));
  };

  const goToToday = () => {
    setSelectedDate(getCurrentDate());
  };

  // Week navigation helpers
  const goToPreviousWeek = () => {
    if (selectedWeek <= 1) {
      setSelectedWeek(52);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedWeek(selectedWeek - 1);
    }
  };

  const goToNextWeek = () => {
    if (selectedWeek >= 52) {
      setSelectedWeek(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedWeek(selectedWeek + 1);
    }
  };

  const goToCurrentWeek = () => {
    setSelectedWeek(getCurrentWeek());
    setSelectedYear(getCurrentYear());
  };

  // Month navigation helpers
  const goToPreviousMonth = () => {
    if (selectedMonth <= 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth >= 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const goToCurrentMonth = () => {
    setSelectedMonth(getCurrentMonth());
    setSelectedYear(getCurrentYear());
  };

  // Year navigation helpers
  const goToPreviousYear = () => {
    setSelectedYear(selectedYear - 1);
  };

  const goToNextYear = () => {
    setSelectedYear(selectedYear + 1);
  };

  const goToCurrentYear = () => {
    setSelectedYear(getCurrentYear());
  };

  const syncAppointments = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      const result = await apiService.runTask('sync_square_appointments');
      const taskId = result.task_id;

      // Poll until the task finishes
      const poll = async () => {
        try {
          const jobs = await apiService.getJobs({ limit: 10 });
          const list = Array.isArray(jobs?.results) ? jobs.results : (Array.isArray(jobs) ? jobs : []);
          const match = list.find(j => j.task_id === taskId);
          if (match && (match.status === 'SUCCESS' || match.status === 'FAILURE')) {
            setSyncing(false);
            let count = 0;
            try {
              const parsed = typeof match.result === 'string' ? JSON.parse(match.result) : match.result;
              count = parsed?.total || 0;
            } catch {}
            setSyncMessage(`Sync complete — ${count} appointment(s) retrieved`);
            setTimeout(() => setSyncMessage(null), 8000);
            fetchAppointments(currentPage);
            return;
          }
          setTimeout(poll, 2000);
        } catch {
          setSyncing(false);
        }
      };
      setTimeout(poll, 2000);
    } catch (err) {
      console.error('Sync error:', err);
      setSyncing(false);
    }
  };

  // Calculate summary statistics from filtered appointments
  const summaryStats = useMemo(() => {
    if (!appointments || appointments.length === 0) {
      return { totalRevenue: 0, expectedRevenue: 0, expectedProfit: 0, technicianPayout: 0, count: 0, rebookCount: 0, rebookTotal: 0 };
    }

    return appointments.reduce((acc, appt) => {
      acc.totalRevenue += parseFloat(appt.total_payment || 0);
      acc.expectedRevenue += parseFloat(appt.expected_revenue || 0);
      acc.expectedProfit += parseFloat(appt.expected_profit || 0);
      acc.technicianPayout += parseFloat(appt.technician_payout || 0);
      acc.count += 1;
      if (appt.status === 'COMPLETED' || appt.status === 'ACCEPTED') {
        acc.rebookTotal += 1;
        if (appt.has_future_appointment) acc.rebookCount += 1;
        if (appt.is_new_client) {
          acc.newClientTotal += 1;
          if (appt.has_future_appointment) acc.newClientRebook += 1;
        }
      }
      return acc;
    }, { totalRevenue: 0, expectedRevenue: 0, expectedProfit: 0, technicianPayout: 0, count: 0, rebookCount: 0, rebookTotal: 0, newClientRebook: 0, newClientTotal: 0 });
  }, [appointments]);

  // Helper function to format client name
  const getClientName = (appointment) => {
    return appointment.client_name || 'N/A';
  };

  // Helper function to format technician name
  const getTechnicianName = (appointment) => {
    return appointment.technician_name || 'N/A';
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // Helper function to format time
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Helper function to calculate total revenue from appointment
  const calculateRevenue = (appointment) => {
    if (appointment.total_payment) {
      return `$${parseFloat(appointment.total_payment).toFixed(2)}`;
    }
    return '$0.00';
  };

  // Helper function to get service type - uses appointment_type from API (computed from services)
  const getServiceType = (appointment) => {
    // Use appointment_type from API which checks if any service is ALF
    return appointment.appointment_type || (appointment.is_alf ? 'ALF' : appointment.is_event ? 'Event' : 'Standard');
  };

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Page Title and Sync Button */}
        {showCreateModal && (
          <CreateAppointmentModal
            onClose={() => setShowCreateModal(false)}
            onCreated={() => fetchAppointments()}
          />
        )}
        <div className="mb-3 flex justify-between items-center">
          <h1 className="text-lg font-semibold text-gray-900 leading-none">Appointments</h1>
          <div className="flex gap-2">
          <Button onClick={() => setShowCreateModal(true)} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1" />
            New
          </Button>
          <Button onClick={syncAppointments} size="sm" variant="secondary" disabled={syncing} className={syncMessage ? 'bg-green-600 hover:bg-green-600' : ''}>
            {syncing ? (
              <>
                <svg className="w-3.5 h-3.5 mr-1.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Syncing...
              </>
            ) : syncMessage ? (
              <>
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {syncMessage}
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sync from Square
              </>
            )}
          </Button>
          </div>
        </div>

        {/* Filters Section */}
        <Panel className="mb-3">
          <div className="space-y-2">
            {/* First Row: Date Type Selector and Quick Actions */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex gap-1 border border-gray-200 rounded">
                <button
                  onClick={() => setDateFilterType('date')}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    dateFilterType === 'date'
                      ? 'bg-red-800 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Date
                </button>
                <button
                  onClick={() => setDateFilterType('week')}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    dateFilterType === 'week'
                      ? 'bg-red-800 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setDateFilterType('month')}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    dateFilterType === 'month'
                      ? 'bg-red-800 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => setDateFilterType('year')}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    dateFilterType === 'year'
                      ? 'bg-red-800 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Year
                </button>
              </div>

              {/* Date navigation buttons */}
              {dateFilterType === 'date' && (
                <div className="flex gap-1">
                  <Button variant="secondary" size="sm" onClick={goToPreviousDay}>
                    ← Prev
                  </Button>
                  <Button size="sm" onClick={goToToday}>
                    Today
                  </Button>
                  <Button variant="secondary" size="sm" onClick={goToNextDay}>
                    Next →
                  </Button>
                </div>
              )}

              {/* Week navigation buttons */}
              {dateFilterType === 'week' && (
                <div className="flex gap-1">
                  <Button variant="secondary" size="sm" onClick={goToPreviousWeek}>
                    ← Prev
                  </Button>
                  <Button size="sm" onClick={goToCurrentWeek}>
                    This Week
                  </Button>
                  <Button variant="secondary" size="sm" onClick={goToNextWeek}>
                    Next →
                  </Button>
                </div>
              )}

              {/* Month navigation buttons */}
              {dateFilterType === 'month' && (
                <div className="flex gap-1">
                  <Button variant="secondary" size="sm" onClick={goToPreviousMonth}>
                    ← Prev
                  </Button>
                  <Button size="sm" onClick={goToCurrentMonth}>
                    This Month
                  </Button>
                  <Button variant="secondary" size="sm" onClick={goToNextMonth}>
                    Next →
                  </Button>
                </div>
              )}

              {/* Year navigation buttons */}
              {dateFilterType === 'year' && (
                <div className="flex gap-1">
                  <Button variant="secondary" size="sm" onClick={goToPreviousYear}>
                    ← Prev
                  </Button>
                  <Button size="sm" onClick={goToCurrentYear}>
                    This Year
                  </Button>
                  <Button variant="secondary" size="sm" onClick={goToNextYear}>
                    Next →
                  </Button>
                </div>
              )}

              {/* Quick Filters */}
              <div className="flex gap-1 ml-auto">
                <button
                  onClick={() => setShowOnlyUnpaid(!showOnlyUnpaid)}
                  className={`px-2 py-1 text-xs font-medium rounded border transition-colors ${
                    showOnlyUnpaid
                      ? 'bg-red-100 text-red-800 border-red-300'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Unpaid Only
                </button>
                <button
                  onClick={() => setShowOnlyUnmatched(!showOnlyUnmatched)}
                  className={`px-2 py-1 text-xs font-medium rounded border transition-colors ${
                    showOnlyUnmatched
                      ? 'bg-orange-100 text-orange-800 border-orange-300'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Unmatched Only
                </button>
                <button
                  onClick={() => setShowCancelled(!showCancelled)}
                  className={`px-2 py-1 text-xs font-medium rounded border transition-colors ${
                    showCancelled
                      ? 'bg-gray-200 text-gray-800 border-gray-400'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Show Cancelled
                </button>
              </div>
            </div>

            {/* Second Row: Date Inputs, Location, Technician */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {/* Date input */}
              {dateFilterType === 'date' && (
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="col-span-2 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                />
              )}

              {/* Week inputs */}
              {dateFilterType === 'week' && (
                <>
                  <input
                    type="number"
                    min="1"
                    max="53"
                    value={selectedWeek}
                    onChange={(e) => setSelectedWeek(parseInt(e.target.value) || 1)}
                    placeholder="Week (1-53)"
                    className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                  />
                  <input
                    type="number"
                    min="2000"
                    max="2100"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value) || getCurrentYear())}
                    placeholder="Year"
                    className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                  />
                </>
              )}

              {/* Month inputs */}
              {dateFilterType === 'month' && (
                <>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                  >
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                  <input
                    type="number"
                    min="2000"
                    max="2100"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value) || getCurrentYear())}
                    placeholder="Year"
                    className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                  />
                </>
              )}

              {/* Year input */}
              {dateFilterType === 'year' && (
                <input
                  type="number"
                  min="2000"
                  max="2100"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value) || getCurrentYear())}
                  placeholder="Year"
                  className="col-span-2 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                />
              )}

              {/* Location Filter */}
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
              >
                <option value="">All Locations</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>

              {/* Technician Filter */}
              <select
                value={selectedTechnician}
                onChange={(e) => setSelectedTechnician(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
              >
                <option value="">All Team Members</option>
                {technicians.map((tech) => (
                  <option key={tech.id} value={tech.id}>
                    {tech.person_name || `${tech.person?.first_name} ${tech.person?.last_name}` || 'Unknown'}
                  </option>
                ))}
              </select>

              {/* Booked By Filter */}
              <select
                value={selectedBookedBy}
                onChange={(e) => setSelectedBookedBy(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
              >
                <option value="">All Bookers</option>
                {[...new Map(
                  appointments
                    .filter(a => a.booked_by_name)
                    .map(a => [a.booked_by_name, a])
                ).values()].map((a) => (
                  <option key={`b-${a.booked_by_name}`} value={a.booked_by_name}>
                    {a.booked_by_name}
                  </option>
                ))}
              </select>

              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
              >
                <option value="all">All Types</option>
                <option value="standard">Standard</option>
                <option value="alf">ALF</option>
                <option value="event">Event</option>
              </select>
            </div>
          </div>
        </Panel>

        {/* Summary Stats - Only show when we have appointments, hidden for coordinator */}
        {!loading && appointments.length > 0 && userRole !== 'coordinator' && (
          <div className="mb-3 grid grid-cols-2 md:grid-cols-6 gap-2">
            <div className="bg-white rounded-lg border border-gray-200 p-2.5">
              <div className="flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-green-600" />
                <span className="text-xs text-gray-500">Total Revenue</span>
              </div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                ${summaryStats.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-2.5">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-xs text-gray-500">Expected Revenue</span>
              </div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                ${summaryStats.expectedRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-2.5">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs text-gray-500">Expected Profit</span>
              </div>
              <div className="mt-1 text-lg font-semibold text-emerald-600">
                ${summaryStats.expectedProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-2.5">
              <div className="flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-orange-600" />
                <span className="text-xs text-gray-500">Team Payout</span>
              </div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                ${summaryStats.technicianPayout.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-2.5">
              <div className="flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5 text-purple-600" />
                <span className="text-xs text-gray-500">Rebook Rate</span>
              </div>
              <div className="mt-1 text-lg font-semibold text-purple-600">
                {summaryStats.rebookTotal > 0 ? Math.round(summaryStats.rebookCount / summaryStats.rebookTotal * 100) : 0}%
                <span className="text-xs font-normal text-gray-400 ml-1">{summaryStats.rebookCount}/{summaryStats.rebookTotal}</span>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-2.5">
              <div className="flex items-center gap-1.5">
                <span className="text-sm">🌟</span>
                <span className="text-xs text-gray-500">New Client Rebook</span>
              </div>
              <div className="mt-1 text-lg font-semibold text-amber-600">
                {summaryStats.newClientTotal > 0 ? Math.round(summaryStats.newClientRebook / summaryStats.newClientTotal * 100) : 0}%
                <span className="text-xs font-normal text-gray-400 ml-1">{summaryStats.newClientRebook}/{summaryStats.newClientTotal}</span>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <Panel className="p-6">
            <LoadingSpinner />
          </Panel>
        )}

        {/* Error State */}
        {error && !loading && (
          <Panel className="bg-red-50 border-red-200 mb-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2">
                <h3 className="text-xs font-medium text-red-800">Error loading appointments</h3>
                <p className="mt-0.5 text-xs text-red-700">{error}</p>
                <button
                  onClick={fetchAppointments}
                  className="mt-1 text-xs font-medium text-red-800 hover:text-red-900"
                >
                  Try again
                </button>
              </div>
            </div>
          </Panel>
        )}

        {/* Appointments Table */}
        {!loading && !error && (
          <>
            {/* Results Summary */}
            <div className="mb-2 text-xs text-gray-600">
              Showing {appointments.length} of {totalCount} appointments
              {currentPage > 1 && ` (Page ${currentPage})`}
            </div>

            <Panel noPadding className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Client</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Team Member</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Location</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Date</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Time</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Type</th>
                      {userRole !== 'coordinator' && <>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Discount</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Exp Rev</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Exp Profit</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Paid</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Amt Paid</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Match</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Tip</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Team Payout</th>
                      </>}
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Booked By</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Booked</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">ETA</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Review</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        <select
                          value={futureFilter}
                          onChange={(e) => setFutureFilter(e.target.value)}
                          className={`text-xs uppercase tracking-wide font-medium bg-transparent border-none cursor-pointer p-0 focus:outline-none ${
                            futureFilter !== 'all' ? 'text-blue-700' : 'text-gray-500'
                          }`}
                        >
                          <option value="all">Rebook</option>
                          <option value="rebooked">Rebooked ✓</option>
                          <option value="not_rebooked">Not Rebooked</option>
                        </select>
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Details</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appointments.length === 0 ? (
                      <tr>
                        <td colSpan={userRole === 'coordinator' ? 9 : 17} className="px-2 py-4 text-center text-xs text-gray-500">
                          No appointments found
                        </td>
                      </tr>
                    ) : (
                      appointments.map((appointment) => (
                        <tr key={appointment.id} className={`hover:bg-gray-50 ${appointment.status?.toUpperCase().includes('CANCEL') ? 'bg-red-50/50 opacity-60' : (appointment.internal_status === 'completed' || appointment.status?.toUpperCase() === 'COMPLETED') ? 'bg-green-50/70' : ''}`}>
                          <td className="px-2 py-1.5 text-xs">
                            {appointment.client ? (
                              <button
                                onClick={() => navigate(`/clients/${appointment.client}`)}
                                className="text-red-700 hover:text-red-900 hover:underline font-medium transition-colors"
                              >
                                {getClientName(appointment)}
                                {appointment.is_new_client && <span className="ml-1" title="New client — first appointment">🌟</span>}
                                {appointment.has_card_on_file && <span className="text-green-500 ml-1" title="Card on file">💳</span>}
                              </button>
                            ) : (
                              <span className="text-gray-900">{getClientName(appointment)}</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-xs">
                            {appointment.technician ? (
                              <button
                                onClick={() => navigate(`/technicians/${appointment.technician}`)}
                                className="text-red-700 hover:text-red-900 hover:underline font-medium transition-colors"
                              >
                                {getTechnicianName(appointment)}
                              </button>
                            ) : (
                              <span className="text-gray-900">{getTechnicianName(appointment)}</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-xs">
                            {appointment.location_id ? (
                              <button
                                onClick={() => navigate(`/locations/${appointment.location_id}`)}
                                className="text-red-700 hover:text-red-900 hover:underline font-medium transition-colors"
                              >
                                {appointment.location_name || 'Unknown'}
                              </button>
                            ) : (
                              <span className="text-gray-900">{appointment.location_name || '-'}</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-gray-900">{formatDate(appointment.date_time)}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-900">{formatTime(appointment.date_time)}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-900">{getServiceType(appointment)}</td>
                          {userRole !== 'coordinator' && <>
                          <td className="px-2 py-1.5 text-xs text-gray-900">
                            {appointment.discount ? `$${parseFloat(appointment.discount).toFixed(2)}` : '$0.00'}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-gray-900">
                            {appointment.expected_revenue ? `$${parseFloat(appointment.expected_revenue).toFixed(2)}` : '$0.00'}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-gray-900">
                            {appointment.expected_profit ? `$${parseFloat(appointment.expected_profit).toFixed(2)}` : '$0.00'}
                          </td>
                          <td className="px-2 py-1.5 text-xs">
                            {appointment.is_paid ? (
                              <span className="text-green-600">✓</span>
                            ) : (
                              <span className="text-red-600">✗</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-gray-900">{calculateRevenue(appointment)}</td>
                          <td className="px-2 py-1.5 text-xs">
                            {appointment.total_payment && appointment.expected_revenue ? (
                              (Math.abs(parseFloat(appointment.total_payment) - parseFloat(appointment.expected_revenue) - parseFloat(appointment.technician_tip || 0)) < 0.01
                                || Math.abs(parseFloat(appointment.total_payment) - parseFloat(appointment.expected_revenue)) < 0.01) ? (
                                <span className="text-green-600">✓</span>
                              ) : (
                                <span className="text-red-600">✗</span>
                              )
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-gray-900">
                            {appointment.technician_tip ? `$${parseFloat(appointment.technician_tip).toFixed(2)}` : '$0.00'}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-gray-900">
                            {appointment.technician_payout ? `$${parseFloat(appointment.technician_payout).toFixed(2)}` : '$0.00'}
                          </td>
                          </>}
                          <td className="px-2 py-1.5 text-xs text-gray-600">
                            {appointment.booked_by_name || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-gray-500">
                            {appointment.creation_date
                              ? new Date(appointment.creation_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                              : '—'}
                          </td>
                          <td className="px-2 py-1.5 text-xs">
                            {appointment.eta_sent && appointment.eta_sent_at && appointment.eta_minutes ? (
                              <span className="text-green-600 font-medium">
                                {new Date(new Date(appointment.eta_sent_at).getTime() + appointment.eta_minutes * 60000)
                                  .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                              </span>
                            ) : appointment.eta_sent ? (
                              <span className="text-green-600">✓</span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-center">
                            {appointment.status?.toUpperCase().includes('CANCEL') ? (
                              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700">Cancelled</span>
                            ) : appointment.has_review ? (
                              <span className="text-green-600">✓</span>
                            ) : (
                              <span className="text-red-600">✗</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-center">
                            {appointment.has_future_appointment ? (
                              <span className="text-green-600 font-medium">✓</span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-xs">
                            <button
                              onClick={() => navigate(`/appointments/${appointment.id}`)}
                              className="text-gray-400 hover:text-red-800 transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>

            {/* Pagination Controls - only for server-side pagination (date filter) */}
            {totalCount > pageSize && (hasNext || hasPrevious || currentPage > 1) && (
              <div className="mt-2 flex items-center justify-between">
                <Button
                  onClick={handlePreviousPage}
                  disabled={!hasPrevious}
                  size="sm"
                  variant={hasPrevious ? 'primary' : 'secondary'}
                >
                  Previous
                </Button>

                <div className="text-xs text-gray-600">
                  Page {currentPage} of {Math.ceil(totalCount / pageSize)}
                </div>

                <Button
                  onClick={handleNextPage}
                  disabled={!hasNext}
                  size="sm"
                  variant={hasNext ? 'primary' : 'secondary'}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}

        <div className="pb-16" />
      </div>
    </div>
  );
}

export default AppointmentsPage; 